<script setup>
import ScheduleProductContentGenerationAction from '@frontJs/components/actions/projectManagement/ai/ScheduleProductContentGenerationAction.vue';
import ToggleProductContentValidatedAtAction from '@frontJs/components/actions/projectManagement/ai/ToggleProductContentValidatedAtAction.vue';
import CheckIcon from '@iconify-vue/ph/check';
import GearIcon from '@iconify-vue/ph/gear-fill';
import TrashIcon from '@iconify-vue/ph/trash-fill';
import XIcon from '@iconify-vue/ph/x';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import ContentComparisonCell from '@js/components/tables/cells/ContentComparisonCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const {
    tenant,
    status,
    customFields,
    productAttributes,
    defaultContextAttributeCodes,
    callback,
} = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    status: {
        type: String,
        required: true,
    },
    customFields: {
        type: Array,
        required: false,
        default: () => [],
    },
    productAttributes: {
        type: Array,
        required: false,
        default: () => [],
    },
    defaultContextAttributeCodes: {
        type: Array,
        required: false,
        default: () => [],
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const hasAiContent = (row) => !!row.aiProductContent;

/**
 * Propriétés d'édition du contenu généré, uniquement pour les champs
 * effectivement demandés à l'IA et dont la génération est terminée.
 */
const editableContentProps = (row, field) => {
    const aiProductContent = row.aiProductContent;

    if (
        !aiProductContent?.completedAt ||
        !aiProductContent.generationFields?.includes(field)
    ) {
        return {};
    }

    return {
        url: route('front.projectManagement.ai.postUpdateProductContent', {
            tenant: tenant.id,
            aiProductContent: aiProductContent.id,
        }),
        payload: {
            field,
        },
    };
};

const onAction = () => {
    if (callback) {
        callback();
    }

    updateData();
};

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
    selectData,
    selectionIds,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'cmsProductId',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID CMS',
                    width: 0,
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.cmsProductId,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'sku',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SKU',
                    width: 0,
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.sku,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'e_commerce_products.type',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Type',
                    width: 0,
                }),
            cell: (props) =>
                h(TextCell, {
                    text:
                        {
                            simple: 'Simple',
                            configurable: 'Configurable',
                            grouped: 'Groupé',
                            bundle: 'Bundle',
                            virtual: 'Virtuel',
                            downloadable: 'Téléchargeable',
                            unknown: 'Inconnu',
                        }[props.row.original.type] || 'Inconnu',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'ean',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'EAN',
                }),
            cell: (props) => {
                const eans = props.row.original.eans || [];

                if (eans.length === 0) {
                    return h(TextCell, { text: '' });
                }

                return h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(
                                'div',
                                { class: 'flex flex-col gap-0.5' },
                                eans.map((ean) =>
                                    h(
                                        'a',
                                        {
                                            href:
                                                'https://www.google.com/search?q=' +
                                                encodeURIComponent(ean),
                                            target: '_blank',
                                            class: 'text-sm text-indigo-600 hover:text-indigo-800 hover:underline',
                                        },
                                        ean,
                                    ),
                                ),
                            ),
                    },
                );
            },
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'e_commerce_product_languages.name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href: props.row.original.url,
                    target: '_blank',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'description',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Description',
                }),
            cell: (props) => {
                const row = props.row.original;

                return h(ContentComparisonCell, {
                    leftContent:
                        row.aiProductContent?.originalDescription ||
                        row.currentDescription ||
                        null,
                    rightContent:
                        row.aiProductContent?.generatedDescription || null,
                    length: 80,
                    richText: true,
                    ...editableContentProps(row, 'description'),
                });
            },
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'shortDescription',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Description courte',
                }),
            cell: (props) => {
                const row = props.row.original;

                return h(ContentComparisonCell, {
                    leftContent:
                        row.aiProductContent?.originalShortDescription ||
                        row.currentShortDescription ||
                        null,
                    rightContent:
                        row.aiProductContent?.generatedShortDescription || null,
                    length: 60,
                    richText: true,
                    ...editableContentProps(row, 'short_description'),
                });
            },
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'metaTitle',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Méta-titre',
                }),
            cell: (props) => {
                const row = props.row.original;

                return h(ContentComparisonCell, {
                    leftContent:
                        row.aiProductContent?.originalMetaTitle || null,
                    rightContent:
                        row.aiProductContent?.generatedMetaTitle || null,
                    length: 60,
                    maxLength: 60,
                    ...editableContentProps(row, 'meta_title'),
                });
            },
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'metaDescription',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Méta-description',
                }),
            cell: (props) => {
                const row = props.row.original;

                return h(ContentComparisonCell, {
                    leftContent:
                        row.aiProductContent?.originalMetaDescription || null,
                    rightContent:
                        row.aiProductContent?.generatedMetaDescription || null,
                    length: 80,
                    maxLength: 155,
                    ...editableContentProps(row, 'meta_description'),
                });
            },
            enableSorting: false,
        }),
        ...customFields.map((field) =>
            columnHelper.accessor(() => null, {
                id: `custom-${field.cmsAttributeCode}`,
                headerCustom: true,
                header: (props) =>
                    h(HeaderCell, {
                        header: props.header,
                        title: field.label,
                    }),
                cell: (props) => {
                    const row = props.row.original;

                    return h(ContentComparisonCell, {
                        leftContent:
                            row.aiProductContent?.originalCustomFields?.[
                                field.cmsAttributeCode
                            ] || null,
                        rightContent:
                            row.aiProductContent?.generatedCustomFields?.[
                                field.cmsAttributeCode
                            ] || null,
                        length: 60,
                        maxLength: field.maxLength || null,
                        ...editableContentProps(row, field.cmsAttributeCode),
                    });
                },
                enableSorting: false,
            }),
        ),
        columnHelper.accessor(() => null, {
            id: 'completedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Généré le',
                    width: 0,
                }),
            cell: (props) =>
                h(TextCell, {
                    text:
                        props.row.original.aiProductContent?.completedAt || '',
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'validatedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Validé le',
                    width: 0,
                }),
            cell: (props) =>
                h(TextCell, {
                    text:
                        props.row.original.aiProductContent?.validatedAt || '',
                }),
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            ...(status === 'toDo' || status === 'toBeApproved'
                                ? [
                                      h(
                                          ScheduleProductContentGenerationAction,
                                          {
                                              tenant,
                                              eCommerceProducts: [
                                                  props.row.original,
                                              ],
                                              aiProductContent:
                                                  props.row.original
                                                      .aiProductContent,
                                              customFields,
                                              productAttributes,
                                              defaultContextAttributeCodes,
                                              callback: onAction,
                                          },
                                          {
                                              trigger: () =>
                                                  h(ActionButton, {
                                                      type: 'primary',
                                                      size: 'small',
                                                      title: 'Programmer la génération de contenu',
                                                      square: true,
                                                      icon: GearIcon,
                                                  }),
                                          },
                                      ),
                                  ]
                                : []),
                            ...(status === 'toBeApproved' ||
                            status === 'approved'
                                ? [
                                      h(
                                          ToggleProductContentValidatedAtAction,
                                          {
                                              tenant,
                                              aiProductContent:
                                                  props.row.original
                                                      .aiProductContent,
                                              callback: onAction,
                                          },
                                          {
                                              button: ({ action }) =>
                                                  h(ActionButton, {
                                                      square: true,
                                                      size: 'small',
                                                      title:
                                                          props.row.original
                                                              .aiProductContent
                                                              .validatedAt ===
                                                          null
                                                              ? 'Valider le contenu généré'
                                                              : 'Annuler la validation du contenu généré',
                                                      type: 'primary',
                                                      callback: () => action(),
                                                      icon:
                                                          props.row.original
                                                              .aiProductContent
                                                              .validatedAt ===
                                                          null
                                                              ? CheckIcon
                                                              : XIcon,
                                                  }),
                                          },
                                      ),
                                  ]
                                : []),
                            ...(hasAiContent(props.row.original)
                                ? [
                                      h(
                                          DeleteAction,
                                          {
                                              url: route(
                                                  'front.projectManagement.ai.deleteProductContents',
                                                  {
                                                      tenant: tenant.id,
                                                  },
                                              ),
                                              config: {
                                                  data: {
                                                      cmsProductIds: [
                                                          props.row.original
                                                              .cmsProductId,
                                                      ],
                                                  },
                                              },
                                              callback: onAction,
                                              title: 'Supprimer le contenu généré',
                                              content:
                                                  "Cette action supprimera le contenu généré par l'IA pour ce produit. Le produit réapparaîtra dans l'onglet « À faire ».",
                                          },
                                          {
                                              trigger: () =>
                                                  h(ActionButton, {
                                                      type: 'danger',
                                                      size: 'small',
                                                      title: 'Supprimer le contenu généré',
                                                      square: true,
                                                      icon: TrashIcon,
                                                  }),
                                          },
                                      ),
                                  ]
                                : []),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.ai.postProductContents', {
        tenant: tenant.id,
    }),
    selectionIdKey: 'cmsProductId',
    data: {
        status,
    },
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
        force-scroll
    >
        <template #actions>
            <ScheduleProductContentGenerationAction
                v-if="status === 'toDo' || status === 'toBeApproved'"
                :tenant="tenant"
                :e-commerce-products="() => selectData()"
                :custom-fields="customFields"
                :product-attributes="productAttributes"
                :default-context-attribute-codes="defaultContextAttributeCodes"
                :callback="onAction"
            >
                <template #trigger>
                    <ActionButton
                        type="primary"
                        size="small"
                        :disabled="selectionIds.length === 0"
                        :left-icon="GearIcon"
                    >
                        Programmer la génération de contenu
                    </ActionButton>
                </template>
            </ScheduleProductContentGenerationAction>

            <DeleteAction
                v-if="status !== 'toDo'"
                :url="
                    route('front.projectManagement.ai.deleteProductContents', {
                        tenant: tenant.id,
                    })
                "
                :resolve-data="
                    () =>
                        selectData().then((products) => ({
                            cmsProductIds: products.map(
                                (product) => product.cmsProductId,
                            ),
                        }))
                "
                :callback="
                    () => {
                        onAction();
                    }
                "
                content="Cette action supprimera les contenus générés par l'IA pour les produits sélectionnés. Les produits réapparaîtront dans l'onglet « À faire »."
            >
                <template #title="{ data }">
                    Supprimer {{ data?.cmsProductIds?.length ?? 0 }} contenu(s)
                    généré(s)
                </template>

                <template #trigger>
                    <ActionButton
                        type="danger"
                        :left-icon="TrashIcon"
                        size="small"
                        title="Supprimer"
                        :disabled="selectionIds.length === 0"
                    >
                        Supprimer
                    </ActionButton>
                </template>
            </DeleteAction>
        </template>
    </DataTableLayout>
</template>
